import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Box,
  Card,
  CardBody,
  Container,
  FormControl,
  FormLabel,
  Heading,
  Stack,
  Text,
  VStack,
  Grid,
  GridItem,
  Image,
  IconButton,
  Checkbox,
  Spinner,
  useToast,
} from '@chakra-ui/react';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { Button } from '../../../components/atoms/Button';
import { Input, SelectInput } from '../../../components/atoms/Input';
import { useGetCompany, useRegisterCompany } from '../../../service/partners/hooks';
import { useCallback, useEffect, useState } from 'react';
import { baseUrl, partiuClient } from '../../../service/axios';
import { capturePartnerApiError } from '../utils/sentryUtils';
import { FaUpload, FaTimes } from 'react-icons/fa';
import { useTheme } from '../../../contexts/ThemeProvider';
import { validarCNPJ, validarCPF } from '@/utils/validateDocuments';
import { masks } from '@/utils/masks';
import { useGetCEPMutation } from '@/service/thirdParty/hooks';
import { ICompanyRegistrationData } from '@/service/partners/api';

// Schema de validação para empresa e loja
const companyStoreSchema = z.object({
  // Dados da empresa
  cnpj: z.string().min(1, 'CNPJ é obrigatório'),
  corporate_name: z.string().min(1, 'Razão social é obrigatória'),
  name: z.string().min(1, 'Nome fantasia é obrigatório'),
  cellphone: z
    .string().min(10, 'Telefone deve ter pelo menos 10 caracteres'),
  category: z.string().min(1, 'Categoria é obrigatória'),
  description: z.string().min(1, 'Descrição é obrigatória'),
  site: z.string().optional(),
  cover_photo: z.any().refine((val) => val !== undefined && val !== null, 'Foto de capa é obrigatória'),
  logo: z.any().refine((val) => val !== undefined && val !== null, 'Logo é obrigatório'),
  exclusive: z.boolean().optional(),
  owner: z.string().optional(),
  email: z.string().optional(),
  // Modo de funcionamento
  mode: z.enum(['local', 'online'], {
    required_error: 'Modo é obrigatório',
  }),

  // Dados da loja/endereço (validação condicional)
  postal_code: z.string().optional(),
  street: z.string().optional(),
  number: z.string().optional(),
  complement: z.string().optional(),
  neighborhood: z.string().optional(),
  state: z.string().optional(),
  city: z.string().optional(),
  country: z.string().optional(),
}).superRefine((data, ctx) => {
  if (data.mode === 'local') {
    if (!data.postal_code || data.postal_code.trim() === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['postal_code'],
        message: 'CEP é obrigatório para loja física',
      });
    }
    if (!data.street || data.street.trim() === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['street'],
        message: 'Endereço é obrigatório para loja física',
      });
    }
    if (!data.number || data.number.trim() === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['number'],
        message: 'Número é obrigatório para loja física',
      });
    }
    if (!data.neighborhood || data.neighborhood.trim() === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['neighborhood'],
        message: 'Bairro é obrigatório para loja física',
      });
    }
    if (!data.state || data.state.trim() === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['state'],
        message: 'Estado é obrigatório para loja física',
      });
    }
    if (!data.city || data.city.trim() === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['city'],
        message: 'Cidade é obrigatória para loja física',
      });
    }
    if (!data.country || data.country.trim() === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['country'],
        message: 'País é obrigatório para loja física',
      });
    }
  }
  // Se for online, site é obrigatório
  if (data.mode === 'online') {
    if (!data.site || data.site.trim() === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['site'],
        message: 'Site é obrigatório para empresas online',
      });
    }
  }
});

type CompanyStoreFormData = z.infer<typeof companyStoreSchema>;

interface StepStoreRegistrationProps {
  onNext: () => void;
  onPrevious: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateData: (data: any) => void;
  isMapApiLoaded?: boolean;
}

export function StepStoreRegistration({ onNext, onPrevious, data, updateData, isMapApiLoaded }: StepStoreRegistrationProps) {
  const { id } = useTheme();
  const { isLoading: isCompanyLoading } = useGetCompany();

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    setError,
    clearErrors,
  } = useForm<CompanyStoreFormData>({
    resolver: zodResolver(companyStoreSchema),
    defaultValues: {
      mode: data.companyMode,
    },
  });

  const isStaging = baseUrl?.includes('staging');

  const [companyExists, setCompanyExists] = useState<{
    checking: boolean;
    exists: boolean;
    message?: string;
    dashboardLink?: string;
  }>({ checking: false, exists: false });

  // Estados para preview das imagens
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);

  const watchedMode = watch('mode');
  const watchedPostalCode = watch('postal_code');
  const watchedStreet = watch('street');
  const watchedNumber = watch('number');
  const watchedCity = watch('city');
  const watchedState = watch('state');
  const watchedCNPJ = watch('cnpj');

  const toast = useToast();
  const [mapCoordinates, setMapCoordinates] = useState<{ lat: number; lng: number } | null>(null);
  const [isMapLoading, setIsMapLoading] = useState(false);

  // Loader centralizado no PartnerWizard, evita duplicar script e erros de remoção de nós

  const fetchAddressCoordinates = useCallback(async () => {
    if (!watchedStreet || !watchedNumber || !watchedCity || !watchedState) {
      setMapCoordinates(null);
      return;
    }

    setIsMapLoading(true);
    try {
      const address = `${watchedStreet}, ${watchedNumber}, ${watchedCity}, ${watchedState}, Brasil`;
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=AIzaSyB5tXrkiapXmzmvkkW0-7MQvfx1LL9VScQ`
      );
      const data = await response.json();

      if (data.results && data.results.length > 0) {
        const { lat, lng } = data.results[0].geometry.location;
        setMapCoordinates({ lat, lng });
      } else {
        setMapCoordinates(null);
      }
    } catch {
      setMapCoordinates(null);
    } finally {
      setIsMapLoading(false);
    }
  }, [watchedStreet, watchedNumber, watchedCity, watchedState]);

  const getCEP = useGetCEPMutation({
    onSuccess: (cepData) => {
      if (cepData) {
        setValue('street', cepData.logradouro);
        setValue('neighborhood', cepData.bairro);
        setValue('city', cepData.localidade);
        setValue('state', cepData.uf);
        setValue('country', 'Brasil');
        fetchAddressCoordinates();
      }
    },
    onError: (error) => {
      // Capturar erro no Sentry
      capturePartnerApiError(error, {
        component: 'StepStoreRegistration',
        step: 'store_registration',
        action: 'fetch_cep',
        postal_code: watchedPostalCode,
      });
    },
  });

  useEffect(() => {
    if (watchedMode === 'local') {
      fetchAddressCoordinates();
    }
  }, [watchedMode, watchedStreet, watchedPostalCode, watchedCity, watchedState, watchedNumber]);

  useEffect(() => {
    if (!watchedPostalCode || watchedMode !== 'local') return;

    const cepUnmasked = masks.unmask(watchedPostalCode || '');

    if (cepUnmasked.length === 8) {
      getCEP.mutate(cepUnmasked);
    }
  }, [watchedPostalCode, watchedMode]);

  const registerCompany = useRegisterCompany({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onSuccess: (companyData: any) => {
      updateData({
        company: companyData.company,
        store: companyData.store,
        companyMode: watchedMode,
      });
      onNext();
    },
    onError: (error: string) => {
      // Capturar erro no Sentry (o erro já foi formatado pelo hook)
      capturePartnerApiError(new Error(error), {
        component: 'StepStoreRegistration',
        step: 'store_registration',
        action: 'register_company',
        company_data: {
          cnpj: watchedCNPJ,
          mode: watchedMode,
        },
      });

      toast({
        title: 'Erro ao registrar empresa',
        description: error,
        status: 'error',
        duration: 8000,
        isClosable: true,
        position: 'top',
      });
    },
  });

  useEffect(() => {
    const cnpjUnmasked = masks.unmask(watchedCNPJ || '');

    if (cnpjUnmasked.length >= 11 && !validarCNPJ(cnpjUnmasked) && !validarCPF(cnpjUnmasked)) {
      setError('cnpj', { message: 'CNPJ/CPF inválido' });
      return;
    } else {
      clearErrors('cnpj');
    }

    if (companyExists.checking) return;

    setCompanyExists({ checking: true, exists: false });
  }, [watchedCNPJ, setError]);

  useEffect(() => {
    if (!watchedCNPJ || masks.unmask(watchedCNPJ).length < 14) return;

    if (validarCNPJ(watchedCNPJ)) {
      clearErrors('cnpj');

      partiuClient.api
        .getCompanyDataFromCNPJ(masks.unmask(watchedCNPJ))
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .then((companyData: any) => {
          if (companyData) {
            setValue('corporate_name', companyData.nome || '');
            setValue('name', companyData.fantasia || '');
            setValue('cellphone', companyData.telefone ? masks.cell(companyData.telefone) : '');
            if (companyData.logradouro) {
              setValue('street', companyData.logradouro);
            }
            if (companyData.numero) {
              setValue('number', masks.number(companyData.numero));
            }
            if (companyData.complemento) {
              setValue('complement', companyData.complemento);
            }
            if (companyData.bairro) {
              setValue('neighborhood', companyData.bairro);
            }
            if (companyData.municipio) {
              setValue('city', companyData.municipio);
            }
            if (companyData.uf) {
              setValue('state', companyData.uf);
            }
            if (companyData.cep) {
              setValue('postal_code', companyData.cep);
            }
          }
        })
        .catch(() => {
          // erro silenciado
        });
    }
  }, [watchedCNPJ, setValue, clearErrors]);

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validar formato do arquivo (somente imagens JPEG, PNG ou WebP)
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: 'Formato inválido. Envie uma imagem JPEG, PNG ou WebP.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        event.target.value = ''; // Limpar o input
        return;
      }

      // Validar tamanho do arquivo (2MB = 2 * 1024 * 1024 bytes)
      const maxSize = 2 * 1024 * 1024; // 2MB em bytes
      if (file.size > maxSize) {
        toast({
          title: 'O arquivo é muito grande. O tamanho máximo permitido é 2MB.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        event.target.value = ''; // Limpar o input
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target?.result as string);
        setValue('logo', file);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCoverUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validar formato do arquivo (somente imagens JPEG, PNG ou WebP)
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: 'Formato inválido. Envie uma imagem JPEG, PNG ou WebP.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        event.target.value = ''; // Limpar o input
        return;
      }

      // Validar tamanho do arquivo (2MB = 2 * 1024 * 1024 bytes)
      const maxSize = 2 * 1024 * 1024; // 2MB em bytes
      if (file.size > maxSize) {
        toast({
          title: 'O arquivo é muito grande. O tamanho máximo permitido é 2MB.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        event.target.value = ''; // Limpar o input
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setCoverPreview(e.target?.result as string);
        setValue('cover_photo', file);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => {
    setLogoPreview(null);
    setValue('logo', undefined);
  };

  const removeCover = () => {
    setCoverPreview(null);
    setValue('cover_photo', undefined);
  };

  const handleSubmitForm = (formData: CompanyStoreFormData) => {

    const registrationData: ICompanyRegistrationData = {
      company: {
        cnpj: masks.unmask(formData.cnpj),
        corporate_name: formData.corporate_name,
        name: formData.name,
        category: formData.category,
        description: formData.description,
        cellphone: masks.unmask(formData.cellphone),
        site: formData.site,
        owner: data.user.full_name,
        email: data.user.email,
      },
      cover_photo: formData.cover_photo,
      logo: formData.logo,
      exclusive: formData.exclusive || false,
      store: {
        postal_code: formData.postal_code ? masks.unmask(formData.postal_code) : '',
        street: formData.street || '',
        number: formData.number || '',
        complement: formData.complement || '',
        neighborhood: formData.neighborhood || '',
        state: formData.state || '',
        city: formData.city || '',
        country: formData.country || '',
      },
    };
    registerCompany.mutate({ data: registrationData, agreementId: String(id) });
  };

  if (isCompanyLoading) {
    return (
      <Container width={'100%'}>
        <VStack spacing={6}>
          <Box textAlign="center">
            <Spinner size="xl" color="blue.500" />
          </Box>
        </VStack>
      </Container>
    );
  }

  if (data.company?.id) {
    return (
      <Container width={'100%'}>
        <VStack spacing={6}>
          <Box textAlign="center">
            <Heading size="lg" color="gray.800" mb={2}>
              ✅ Empresa Encontrada!
            </Heading>
            <Text color="gray.600" fontSize="md">
              Sua empresa <strong>{data.company.name}</strong> já está cadastrada em nossa plataforma.
            </Text>
            <Text color="gray.500" fontSize="sm" mt={2}>
              Se precisar alterar algum dado, ou cadastrar um desconto, acesse o PDV da sua empresa.
            </Text>
          </Box>

          <VStack spacing={3} w="full">
            <Button
              colorScheme="blue"
              variant="outline"
              size="lg"
              w="full"
              onClick={() =>
                window.open(`https://pdv.${isStaging ? 'beta.' : ''}partiu.com.br/login/${data.token}`, '_blank')
              }
            >
              🔧 Acessar PDV para Editar Dados
            </Button>

            <Button colorScheme="blue" size="lg" onClick={onNext} w="full">
              Continuar
            </Button>
            <Button
              type="button"
              variant="label"
              onClick={onPrevious}
              flex={1}
              color={'#5e5e5b'}
              border="none"
              backgroundColor="transparent"
            >
              Voltar
            </Button>
          </VStack>
        </VStack>
      </Container>
    );
  }

  return (
    <Container width={'100%'}>
      <VStack spacing={6}>
        <Box textAlign="center">
          <Heading size="lg" color="gray.800" mb={2}>
            Empresa & Loja
          </Heading>
          <Text color="gray.600">Registre os dados da sua empresa e endereço da loja</Text>
        </Box>

        <Card w="full" shadow="lg" p={4}>
          <CardBody>
            <form onSubmit={handleSubmit(handleSubmitForm)}>
              <VStack spacing={6}>
                {/* Dados da Empresa */}
                <Box w="full">
                  <Heading size="md" color="gray.800" mb={4}>
                    📋 Dados da Empresa
                  </Heading>

                  <Grid w="full" templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={6} mb={6}>
                    <GridItem>
                      <FormControl isInvalid={!!errors.logo}>
                        <FormLabel fontWeight="medium" color="gray.700" mb={3}>
                          🖼️ Logo da Empresa *
                        </FormLabel>
                        {logoPreview ? (
                          <Box position="relative" w="full">
                            <Image
                              src={logoPreview}
                              alt="Logo preview"
                              w="120px"
                              h="120px"
                              objectFit="contain"
                              border="2px solid"
                              borderColor="gray.200"
                              borderRadius="lg"
                              mx="auto"
                            />
                            <IconButton
                              aria-label="Remover logo"
                              icon={<FaTimes />}
                              size="sm"
                              colorScheme="red"
                              position="absolute"
                              top={2}
                              right={2}
                              onClick={removeLogo}
                            />
                          </Box>
                        ) : (
                          <Box
                            border="2px dashed"
                            borderColor="gray.300"
                            borderRadius="lg"
                            p={6}
                            textAlign="center"
                            cursor="pointer"
                            _hover={{ borderColor: 'blue.400', bg: 'blue.50' }}
                            transition="all 0.2s"
                            h="150px"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <input
                              type="file"
                              accept="image/jpeg,image/png,image/webp"
                              onChange={handleLogoUpload}
                              style={{ display: 'none' }}
                              id="logo-upload"
                            />
                            <label htmlFor="logo-upload" style={{ cursor: 'pointer' }}>
                              <VStack spacing={2}>
                                <FaUpload size={24} color="#718096" />
                                <Text color="gray.600" fontSize="sm">
                                  Clique para fazer upload do logo
                                </Text>
                                <Text color="gray.500" fontSize="xs" whiteSpace="normal" textAlign="center">
                                  Tamanho recomendado: 250px x 250px
                                </Text>
                                <Text color="gray.500" fontSize="xs" whiteSpace="normal" textAlign="center">
                                  Tamanho máximo: 2MB
                                </Text>
                              </VStack>
                            </label>
                          </Box>
                        )}
                        {errors.logo && (
                          <Text color="red.500" fontSize="sm" mt={1}>
                            Logo é obrigatório
                          </Text>
                        )}
                      </FormControl>
                    </GridItem>

                    <GridItem>
                      <FormControl isInvalid={!!errors.cover_photo}>
                        <FormLabel fontWeight="medium" color="gray.700" mb={3}>
                          🖼️ Foto de Capa da Empresa *
                        </FormLabel>
                        {coverPreview ? (
                          <Box position="relative" w="full">
                            <Image
                              src={coverPreview}
                              alt="Cover preview"
                              w="full"
                              h="120px"
                              objectFit="cover"
                              border="2px solid"
                              borderColor="gray.200"
                              borderRadius="lg"
                            />
                            <IconButton
                              aria-label="Remover cover"
                              icon={<FaTimes />}
                              size="sm"
                              colorScheme="red"
                              position="absolute"
                              top={2}
                              right={2}
                              onClick={removeCover}
                            />
                          </Box>
                        ) : (
                          <Box
                            border="2px dashed"
                            borderColor="gray.300"
                            borderRadius="lg"
                            p={6}
                            textAlign="center"
                            cursor="pointer"
                            _hover={{ borderColor: 'blue.400', bg: 'blue.50' }}
                            transition="all 0.2s"
                            h="150px"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <input
                              type="file"
                              accept="image/jpeg,image/png,image/webp"
                              onChange={handleCoverUpload}
                              style={{ display: 'none' }}
                              id="cover-upload"
                            />
                            <label htmlFor="cover-upload" style={{ cursor: 'pointer' }}>
                              <VStack spacing={2}>
                                <FaUpload size={24} color="#718096" />
                                <Text color="gray.600" fontSize="sm">
                                  Clique para fazer upload da foto de capa
                                </Text>
                                <Text color="gray.500" fontSize="xs" whiteSpace="normal" textAlign="center">
                                  Tamanho recomendado: 1080px x 764px
                                </Text>
                                <Text color="gray.500" fontSize="xs" whiteSpace="normal" textAlign="center">
                                  Tamanho máximo: 2MB
                                </Text>
                              </VStack>
                            </label>
                          </Box>
                        )}
                        {errors.cover_photo && (
                          <Text color="red.500" fontSize="sm" mt={1}>
                            Foto de capa é obrigatória
                          </Text>
                        )}
                      </FormControl>
                    </GridItem>
                  </Grid>
                  <VStack spacing={4}>
                    <FormControl isInvalid={!!errors.cnpj}>
                      <Input
                        type="text"
                        mask="document"
                        label="CNPJ/CPF"
                        placeholder="CPF ou CNPJ"
                        name="cnpj"
                        control={control}
                      />
                    </FormControl>

                    <FormControl isInvalid={!!errors.corporate_name}>
                      <Input
                        type="text"
                        placeholder="Razão social da empresa"
                        label="Razão Social"
                        name="corporate_name"
                        control={control}
                      />
                    </FormControl>
                    <FormControl isInvalid={!!errors.name}>
                      <Input
                        type="text"
                        placeholder="Nome fantasia (como aparecerá no app)"
                        label="Nome Fantasia"
                        name="name"
                        control={control}
                      />
                    </FormControl>

                    <FormControl isInvalid={!!errors.cellphone}>
                      <Input
                        type="text"
                        placeholder="(00) 00000-0000"
                        mask="cell"
                        label="Telefone"
                        name="cellphone"
                        control={control}
                      />
                    </FormControl>

                    <FormControl isInvalid={!!errors.category}>
                      <SelectInput
                        name="category"
                        control={control}
                        label="Categoria"
                        placeholder="Escolha uma categoria"
                        autoWidth={false}
                        multiple={false}
                        native={false}
                        options={[
                          { value: 'Cinema', label: 'Cinema' },
                          { value: 'Vestuário', label: 'Vestuário' },
                          { value: 'Beleza', label: 'Beleza' },
                          { value: 'Turismo', label: 'Turismo' },
                          { value: 'Educação', label: 'Educação' },
                          { value: 'Para Casa', label: 'Para Casa' },
                          { value: 'Farmácia', label: 'Farmácia' },
                          { value: 'Alimentação', label: 'Alimentação' },
                          { value: 'Saúde', label: 'Saúde' },
                          { value: 'Serviços', label: 'Serviços' },
                          { value: 'Petshop', label: 'Petshop' },
                          { value: 'Para o meu negócio', label: 'Para o meu negócio' },
                          { value: 'Automotivo', label: 'Automotivo' },
                          { value: 'Delivery', label: 'Delivery' },
                          { value: 'Diversão', label: 'Diversão' },
                          { value: 'Outros', label: 'Outros' },
                        ]}
                      />
                    </FormControl>

                    <FormControl isInvalid={!!errors.description}>
                      <Input
                        type="text"
                        placeholder="Conte um pouco sobre sua empresa"
                        label="Descrição"
                        name="description"
                        control={control}
                      />
                    </FormControl>

                    <FormControl isInvalid={!!errors.site}>
                      <Input
                        type="url"
                        placeholder="https://seusite.com.br"
                        label="Site (opcional caso seja loja física)"
                        name="site"
                        control={control}
                      />
                    </FormControl>
                  </VStack>
                </Box>
                {/* Modo de Funcionamento */}
                <Box w="full">
                  <Heading size="md" color="gray.800" mb={4}>
                    🏢 Modo de Funcionamento
                  </Heading>

                  <VStack spacing={4} w="full">
                    <FormControl isInvalid={!!errors.mode} w="full">
                      <FormLabel fontWeight="medium" color="gray.700" mb={3}>
                        Como sua empresa funciona?
                      </FormLabel>
                      <Controller
                        name="mode"
                        control={control}
                        render={({ field }) => (
                          <Stack direction={{ base: 'column', md: 'row' }} spacing={6} w="full">
                            {[
                              {
                                value: 'local',
                                icon: '🏪',
                                title: 'Loja Física',
                                desc: 'Empresa com endereço físico para atendimento presencial',
                              },
                              {
                                value: 'online',
                                icon: '🌐',
                                title: 'Online',
                                desc: 'Empresa digital sem endereço físico',
                              },
                            ].map((option) => (
                              <Box
                                key={option.value}
                                as="button"
                                type="button"
                                onClick={() => field.onChange(option.value)}
                                p={4}
                                border="2px solid"
                                borderColor={field.value === option.value ? 'blue.500' : 'gray.200'}
                                borderRadius="lg"
                                bg={field.value === option.value ? 'blue.50' : 'white'}
                                boxShadow={field.value === option.value ? 'md' : 'none'}
                                transition="all 0.2s"
                                minH="120px"
                                w="full"
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                                justifyContent="center"
                                _hover={{
                                  borderColor: 'blue.300',
                                  bg: field.value === option.value ? 'blue.50' : 'gray.50',
                                }}
                                cursor="pointer"
                              >
                                <Box fontSize="2xl" color={field.value === option.value ? 'blue.500' : 'gray.600'}>
                                  {option.icon}
                                </Box>
                                <Text fontWeight="semibold" color="gray.800" mt={2}>
                                  {option.title}
                                </Text>
                                <Text fontSize="sm" color="gray.600" textAlign="center" mt={1}>
                                  {option.desc}
                                </Text>
                              </Box>
                            ))}
                          </Stack>
                        )}
                      />
                    </FormControl>
                  </VStack>
                </Box>

                {/* Informação para empresas online */}
                {watchedMode === 'online' && (
                  <Box w="full">
                    <Box
                      p={4}
                      bg="blue.50"
                      border="1px solid"
                      borderColor="blue.200"
                      borderRadius="lg"
                      textAlign="center"
                    >
                      <VStack spacing={2}>
                        <Box fontSize="2xl">🌐</Box>
                        <Text fontWeight="semibold" color="blue.800">
                          Empresa Digital
                        </Text>
                        <Text fontSize="sm" color="blue.700">
                          Como sua empresa funciona online, não é necessário informar um endereço físico. Os clientes
                          entrarão em contato através do telefone ou site informados.
                        </Text>
                      </VStack>
                    </Box>
                  </Box>
                )}

                {/* Dados da Loja/Endereço - Mostrar apenas se for loja física */}
                {watchedMode === 'local' && (
                  <Box w="full">
                    <Heading size="md" color="gray.800" mb={4}>
                      📍 Endereço da Loja
                    </Heading>
                    <Text color="gray.600" fontSize="sm" mb={4}>
                      Informe o endereço físico da sua empresa para que os clientes possam encontrá-la
                    </Text>

                    <VStack spacing={4}>
                      <FormControl isInvalid={!!errors.postal_code}>
                        <Input
                          type="text"
                          placeholder="00000-000"
                          mask="zipcode"
                          label="CEP"
                          name="postal_code"
                          control={control}
                          isDisabled={getCEP.isLoading}
                        />
                        {getCEP.isLoading && (
                          <Text fontSize="sm" color="blue.500" mt={1}>
                            🔍 Buscando endereço...
                          </Text>
                        )}
                      </FormControl>

                      <Grid w="full" templateColumns={{ base: '1fr', md: '2fr 1fr' }} gap={4}>
                        <GridItem>
                          <FormControl isInvalid={!!errors.street}>
                            <Input
                              type="text"
                              placeholder="Rua/Avenida"
                              label="Rua"
                              name="street"
                              control={control}
                              isDisabled={getCEP.isLoading}
                            />
                            {getCEP.isLoading && (
                              <Text fontSize="sm" color="gray.500" mt={1}>
                                ⏳ Preenchido automaticamente pelo CEP
                              </Text>
                            )}
                          </FormControl>
                        </GridItem>
                        <GridItem>
                          <FormControl isInvalid={!!errors.number}>
                            <Input
                              type="text"
                              placeholder="Número"
                              mask="number"
                              label="Número"
                              name="number"
                              control={control}
                            />
                          </FormControl>
                        </GridItem>
                      </Grid>

                      <FormControl>
                        <Input
                          type="text"
                          placeholder="Complemento (opcional)"
                          label="Complemento (opcional)"
                          name="complement"
                          control={control}
                        />
                      </FormControl>

                      <FormControl isInvalid={!!errors.neighborhood}>
                        <Input
                          type="text"
                          placeholder="Bairro"
                          label="Bairro"
                          name="neighborhood"
                          control={control}
                          isDisabled={getCEP.isLoading}
                        />
                        {getCEP.isLoading && (
                          <Text fontSize="sm" color="gray.500" mt={1}>
                            ⏳ Preenchido automaticamente pelo CEP
                          </Text>
                        )}
                      </FormControl>

                      <Grid w="full" templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4}>
                        <GridItem>
                          <FormControl isInvalid={!!errors.city}>
                            <Input
                              type="text"
                              placeholder="Cidade"
                              label="Cidade"
                              name="city"
                              control={control}
                              isDisabled={getCEP.isLoading}
                            />
                            {getCEP.isLoading && (
                              <Text fontSize="sm" color="gray.500" mt={1}>
                                ⏳ Preenchido automaticamente pelo CEP
                              </Text>
                            )}
                          </FormControl>
                        </GridItem>
                        <GridItem w="full">
                          <FormControl isInvalid={!!errors.state}>
                            <Input
                              type="text"
                              placeholder="Estado"
                              label="Estado"
                              name="state"
                              control={control}
                              isDisabled={getCEP.isLoading}
                            />
                            {getCEP.isLoading && (
                              <Text fontSize="sm" color="gray.500" mt={1}>
                                ⏳ Preenchido automaticamente pelo CEP
                              </Text>
                            )}
                          </FormControl>
                        </GridItem>
                      </Grid>

                      <FormControl isInvalid={!!errors.country}>
                        <Input type="text" placeholder="País" label="País" name="country" control={control} />
                      </FormControl>

                      {/* Mapa do Google Maps */}
                      {watchedMode === 'local' && isMapApiLoaded && (
                        <Box w="full">
                          <Text fontWeight="medium" color="gray.700" mb={3}>
                            📍 Localização no Mapa
                          </Text>

                          {isMapLoading ? (
                            <Box
                              w="full"
                              h="300px"
                              bg="gray.100"
                              borderRadius="lg"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                            >
                              <Text color="gray.500">Carregando mapa...</Text>
                            </Box>
                          ) : mapCoordinates ? (
                            <Box
                              w="full"
                              h="300px"
                              borderRadius="lg"
                              overflow="hidden"
                              border="1px solid"
                              borderColor="gray.200"
                            >
                              <GoogleMap
                                mapContainerStyle={{
                                  width: '100%',
                                  height: '100%',
                                }}
                                center={mapCoordinates}
                                zoom={15}
                                options={{
                                  zoomControl: true,
                                  streetViewControl: false,
                                  mapTypeControl: false,
                                  fullscreenControl: false,
                                }}
                              >
                                <Marker position={mapCoordinates} />
                              </GoogleMap>
                            </Box>
                          ) : (
                            <Box
                              w="full"
                              h="300px"
                              bg="gray.50"
                              borderRadius="lg"
                              border="2px dashed"
                              borderColor="gray.300"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                            >
                              <VStack spacing={2}>
                                <Text fontSize="2xl">🗺️</Text>
                                <Text color="gray.500" textAlign="center">
                                  Preencha o endereço completo para visualizar a localização no mapa
                                </Text>
                              </VStack>
                            </Box>
                          )}
                        </Box>
                      )}
                    </VStack>
                    <FormControl w="full" mt={4}>
                      <Controller
                        name="exclusive"
                        control={control}
                        render={({ field }) => (
                          <Checkbox
                            isChecked={field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                            colorScheme="blue"
                          >
                            Convênio exclusivo (não aceitar outros convênios)
                          </Checkbox>
                        )}
                      />
                    </FormControl>
                  </Box>
                )}

                <Button
                  type="submit"
                  disabled={registerCompany.isLoading}
                  flex={1}
                  isLoading={registerCompany.isLoading}
                  loadingText="Registrando..."
                >
                  Continuar
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={onPrevious}
                  flex={1}
                  color={'#5e5e5b'}
                  border="none"
                  backgroundColor="white"
                >
                  Voltar
                </Button>
              </VStack>
            </form>
          </CardBody>
        </Card>
      </VStack>
    </Container>
  );
}
